<template>
    <v-container class="ticket-assist-page pa-2" fluid>
        <v-toolbar dense flat class="mb-2">
            <v-toolbar-title class="text-subtitle-1">{{ $t('ticket.assist.title') }}</v-toolbar-title>
            <v-spacer />
            <v-btn
                v-if="token"
                color="primary"
                small
                class="ml-2 flex-shrink-0"
                @click="openAssistNewTicketDialog"
            >
                <v-icon left small>mdi-plus</v-icon>
                {{ $t('ticket.createDialog.title') }}
            </v-btn>
        </v-toolbar>

        <v-stepper v-model="wizardStep" vertical class="elevation-1">
            <v-stepper-step :complete="wizardStep > 1" step="1">
                <div class="stepper-step-title">{{ $t('ticket.assist.stepAction') }}</div>
                <div v-if="assistActionSummaryText" class="text-caption grey--text stepper-step-selected">
                    {{ assistActionSummaryText }}
                </div>
            </v-stepper-step>
            <v-stepper-content step="1">
                <v-card
                    v-for="opt in assistActionOptions"
                    :key="opt.value"
                    outlined
                    class="pa-3 mb-2"
                    :class="{ 'primary lighten-5': assistAction === opt.value }"
                    style="cursor: pointer"
                    @click="selectAssistAction(opt.value)"
                >
                    <div class="text-body-1 font-weight-medium">{{ $t(opt.labelKey) }}</div>
                    <div class="text-caption grey--text">{{ $t('ticket.assist.tapToContinue') }}</div>
                </v-card>
            </v-stepper-content>

            <template v-if="assistAction">
                <v-stepper-step :complete="wizardStep > 2" step="2">
                    <div class="stepper-step-title">{{ $t(assistStep2LabelKey) }}</div>
                    <div v-if="assistStep2SelectionSummary" class="text-caption grey--text stepper-step-selected">
                        {{ assistStep2SelectionSummary }}
                    </div>
                </v-stepper-step>
                <v-stepper-content step="2">
                <v-radio-group
                    v-if="assistAction === 'maintenance'"
                    v-model="maintenanceRefType"
                    class="maintenance-ref-type-radios mt-0 mb-2"
                    row
                    hide-details
                    @change="onMaintenanceRefTypeChange"
                >
                    <v-radio :label="$t('ticket.assist.maintenanceByEquip')" value="equip" />
                    <v-radio :label="$t('ticket.assist.maintenanceByVehicle')" value="vehicle" />
                </v-radio-group>

                <v-autocomplete
                    v-if="showEquipSelectOnStep2"
                    v-model="selectedItemKey"
                    :items="mergedEquipKitItems"
                    :loading="loadingEquipKits"
                    item-text="displayText"
                    item-value="itemKey"
                    class="equip-kit-autocomplete"
                    outlined
                    dense
                    hide-no-data
                    :label="$t('ticket.assist.selectEquipKit')"
                    :filter="filterEquipKit"
                    @change="onEquipKitSelected"
                >
                    <template v-slot:append-outer>
                        <v-btn icon color="primary" @click="openQrScanner" :title="$t('ticket.assist.scanQr')">
                            <v-icon>mdi-qrcode-scan</v-icon>
                        </v-btn>
                    </template>
                </v-autocomplete>

                <v-autocomplete
                    v-if="assistAction === 'maintenance' && maintenanceRefType === 'vehicle'"
                    v-model="selectedVehicleId"
                    :items="veiculosList"
                    :loading="loadingVeiculos"
                    item-text="displayText"
                    item-value="id_veiculo"
                    class="mt-2"
                    outlined
                    dense
                    clearable
                    :label="$t('ticket.assist.selectVehicle')"
                />

                <v-btn color="primary" class="mt-2" :disabled="!canProceedFromEquipStep" @click="onEquipStepNext">
                    {{ $t('button.next') }}
                </v-btn>
                <v-btn text class="mt-2 d-block" @click="backFromEquipToAction">{{ $t('button.back') }}</v-btn>
                </v-stepper-content>

                <v-stepper-step :complete="wizardStep > 3" step="3">
                    <div class="stepper-step-title">{{ $t(assistStep3LabelKey) }}</div>
                    <div v-if="assistStep3HeaderSummary" class="text-caption grey--text stepper-step-selected">
                        {{ assistStep3HeaderSummary }}
                    </div>
                </v-stepper-step>
                <v-stepper-content step="3">
                <template v-if="wizardStep === 3 && assistAction === 'vehicle'">
                    <v-autocomplete
                        v-model="selectedVehicleId"
                        :items="veiculosList"
                        :loading="loadingVeiculos"
                        item-text="displayText"
                        item-value="id_veiculo"
                        outlined
                        dense
                        clearable
                        :label="$t('ticket.assist.selectVehicle')"
                        @change="onVehicleChanged"
                    />
                    <v-btn text class="mr-2" @click="backToAssistActionStep">{{ $t('button.back') }}</v-btn>
                    <v-btn color="primary" :disabled="!selectedVehicleId" @click="goToTicketsStep">
                        {{ $t('ticket.assist.showTickets') }}
                    </v-btn>
                </template>
                <template v-else-if="wizardStep === 3 && assistAction === 'maintenance'">
                    <div v-if="loadingTickets" class="text-center py-6">
                        <v-progress-circular indeterminate color="primary" />
                    </div>
                    <v-alert v-else-if="ticketsError" type="error" dense>{{ ticketsError }}</v-alert>
                    <div v-else-if="!assistTickets.length" class="text-body-2 grey--text py-4">
                        {{ $t('ticket.assist.noOpenTicketsMaintenance') }}
                    </div>
                    <div v-else>
                        <p class="text-caption grey--text mb-2">{{ $t('ticket.assist.openTicketsHint') }}</p>
                        <v-card
                            v-for="t in assistTickets"
                            :key="'tk-m-' + t.id_ticket"
                            outlined
                            class="mb-3 pa-3"
                            @click="openTicketDetail(t)"
                            style="cursor: pointer"
                        >
                            <div class="text-subtitle-2">{{ t.cod_ticket || ('#' + t.id_ticket) }} — {{ t.titulo || '-' }}</div>
                            <div class="text-body-2">{{ t.status_extenso || '-' }}</div>
                            <div class="text-caption grey--text">{{ t.dt_inclusao_extenso || '' }}</div>
                        </v-card>
                    </div>
                    <v-btn text class="mt-2" @click="backToAssistActionStep">{{ $t('button.back') }}</v-btn>
                </template>
                </v-stepper-content>
            </template>

            <!-- Passo 4: só fluxo "Associar a veículo" (manutenção termina no passo 3) -->
            <template v-if="assistAction === 'vehicle'">
                <v-stepper-step step="4">
                    <div class="stepper-step-title">{{ $t('ticket.assist.stepTickets') }}</div>
                    <div v-if="assistVehicleFlowTicketsStepSummary" class="text-caption grey--text stepper-step-selected">
                        {{ assistVehicleFlowTicketsStepSummary }}
                    </div>
                </v-stepper-step>
                <v-stepper-content step="4">
                    <template v-if="wizardStep === 4 && assistAction === 'vehicle'">
                        <div v-if="loadingTickets" class="text-center py-6">
                            <v-progress-circular indeterminate color="primary" />
                        </div>
                        <v-alert v-else-if="ticketsError" type="error" dense>{{ ticketsError }}</v-alert>
                        <div v-else-if="!assistTickets.length" class="text-body-2 grey--text py-4">
                            {{ $t('ticket.assist.noOpenTickets') }}
                        </div>
                        <div v-else>
                            <p class="text-caption grey--text mb-2">{{ $t('ticket.assist.openTicketsHint') }}</p>
                            <v-card
                                v-for="t in assistTickets"
                                :key="'tk-' + t.id_ticket"
                                outlined
                                class="mb-3 pa-3"
                                @click="openTicketDetail(t)"
                                style="cursor: pointer"
                            >
                                <div class="text-subtitle-2">{{ t.cod_ticket || ('#' + t.id_ticket) }} — {{ t.titulo || '-' }}</div>
                                <div class="text-body-2">{{ t.status_extenso || '-' }}</div>
                                <div class="text-caption grey--text">{{ t.dt_inclusao_extenso || '' }}</div>
                            </v-card>
                        </div>
                        <v-btn text class="mt-2" @click="wizardStep = 3">{{ $t('button.back') }}</v-btn>
                    </template>
                </v-stepper-content>
            </template>
        </v-stepper>

        <v-dialog v-model="qrScannerDialog" max-width="560" persistent>
            <v-card>
                <v-toolbar dark color="primary" dense>
                    <v-toolbar-title>{{ $t('ticket.assist.scanQr') }}</v-toolbar-title>
                    <v-spacer />
                    <v-btn icon dark @click="closeQrScanner"><v-icon>mdi-close</v-icon></v-btn>
                </v-toolbar>
                <v-card-text class="pt-4">
                    <v-alert v-if="qrScannerError" type="error" dense class="mb-2">{{ qrScannerError }}</v-alert>
                    <div v-if="qrScannerDialog" class="qr-wrap">
                        <qrcode-stream
                            @decode="onQrDecode"
                            @init="onQrInit"
                            :camera="qrCamera"
                            style="width: 100%; border-radius: 8px;"
                        />
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <ticket-detail-drawer
            ref="ticketDetailDrawer"
            v-model="drawerOpen"
            :ticket="selectedTicket"
            :ticket-id="selectedTicket && selectedTicket.id_ticket"
            :token="token"
            :user="user"
            data-source="colaborador"
            :status="statusList"
            :visible-detail-fields="visibleDetailFields"
            :edit-checklist="true"
            initial-detail-tab="tasks"
            :assist-status-dt-inicio="assistEquipSelectedAt"
            :assist-prefill-equipamento-id="assistPrefillEquipamentoId"
            :show-tasks-tab-status-cta="true"
            :show-close-ticket-button="false"
            :can-edit-schedule="podeEditarAgendamento"
            @update:status="onDetailStatusChange"
            @update:statusAtribuido="onStatusAtribuidoChange"
            @update:statusDispatcher="onStatusDispatcherChange"
            @update:team="onDetailTeamChange"
            @update:schedule="onDetailScheduleChange"
            @update:expectedFinalization="onDetailExpectedFinalizationChange"
            @update:slaManual="onDetailSlaManualChange"
            @update:user="onDetailUserChange"
            @update:tipoTicket="onDetailTipoTicketChange"
            @update:familia="onDetailFamiliaChange"
            @update:tipoAtendimento="onDetailTipoAtendimentoChange"
            @update:servico="onDetailServicoChange"
            @open-checklist-dialog="onOpenChecklistDialog"
            @updated="onDetailUpdated"
            @vehicle-equipment-pairs-change="onVehicleEquipmentPairsChange"
        />

        <checklist-fulfillment-dialog
            v-model="checklistDialog"
            :ticket="currentChecklistTicket"
            :token="token"
            :user="user"
            :checklists="checklists"
            @saved="onChecklistSaved"
        />

        <ticket-create-dialog
            v-if="editedIndex === -1"
            v-model="createTicketDialog"
            screen="criados"
            :edited-item.sync="editedItem"
            :categorias="categorias"
            :checklists="checklists"
            :clientes="clientes"
            :use-clientes="true"
            :empresa-value="empresa"
            @update:empresa="empresa = $event"
            :current-user="user"
            :display-date="displayDate"
            :display-time="displayTime"
            @update:displayDate="displayDate = $event"
            @update:displayTime="displayTime = $event"
            :show-date-time-fields="false"
            :tipos-tickets="tiposTickets"
            :selected-service-usa-veiculo="selectedServiceUsaVeiculoAssist"
            :selected-id-empresa="selectedIdEmpresaAssist"
            :selected-service-usa-equipamento="selectedServiceUsaEquipamentoAssist"
            :selected-service-usa-kit="selectedServiceUsaKitAssist"
            :vehicle-equipment-pairs.sync="vehicleEquipmentPairs"
            :equipamentos-filtrados-por-empresa="equipamentosFiltradosPorServicoAssist"
            :veiculos-filtrados-por-empresa="veiculosFiltradosPorEmpresaCreate"
            :loading-equipamentos="loadingEquipamentosCreate"
            :loading-veiculos="loadingVeiculosCreate"
            :vehicle-equipment-pairs-valid="vehicleEquipmentPairsValidAssist"
            :get-equipamento-display-text-by-id="(id) => getEquipamentoDisplayTextByIdAssist(id)"
            :token="token"
            :loading-save.sync="loadingSave"
            :csv-errors-open.sync="csvErrorsOpen"
            @add-pair="addVehiclePairAssist"
            @remove-pair="removeVehiclePairAssist"
            @duplicate-pair="duplicateVehiclePairAssist"
            @save="saveAssistNewTicket()"
            @agency-saved="onAgencyTicketSaved"
        />
    </v-container>
</template>

<script>
import apiController from '@/utils/apiController';
import EventBus from '../../utils/eventBus';
import { filterTicketStatusesByPerfil } from '@/utils/ticketStatusPerfilFilter';
import { QrcodeStream } from 'vue-qrcode-reader';
import TicketDetailDrawer from './TicketDetailDrawer.vue';
import ChecklistFulfillmentDialog from './ChecklistFulfillmentDialog.vue';
import TicketCreateDialog from './TicketCreateDialog.vue';
import { getTicketCapabilities } from '@/utils/ticketPermissions';

export default {
    name: 'TicketAssistPage',
    components: { QrcodeStream, TicketDetailDrawer, ChecklistFulfillmentDialog, TicketCreateDialog },
    data() {
        return {
            user: null,
            token: null,
            wizardStep: 1,
            /** ticket_campos_detalhe screen: colaborador (wizard) vs supervisor (after creating a ticket here). */
            assistDetailScreen: 'colaborador',
            /** Drives which UI is mounted from step 3 onward (set in step 2). */
            assistAction: null,
            /** maintenance only: tickets by equip list vs by vehicle */
            maintenanceRefType: 'equip',
            equipamentosRaw: [],
            kitsRaw: [],
            loadingEquipKits: false,
            selectedItemKey: null,
            assistEquipSelectedAt: null,
            veiculosList: [],
            loadingVeiculos: false,
            selectedVehicleId: null,
            assistTickets: [],
            loadingTickets: false,
            ticketsError: null,
            drawerOpen: false,
            selectedTicket: null,
            statusList: [],
            detailSaveTimer: null,
            detailPendingPatch: null,
            detailSaving: false,
            qrScannerDialog: false,
            qrScannerError: null,
            qrCamera: 'auto',
            checklistDialog: false,
            currentChecklistTicket: null,
            checklists: [],
            createTicketDialog: false,
            editedIndex: -1,
            loadingSave: false,
            csvErrorsOpen: false,
            operacao: 0,
            empresa: null,
            editedItem: {
                id_ticket: null,
                titulo: '',
                corpo: '',
                id_categoria: null,
                id_status: null,
                id_status_atribuido: null,
                id_status_dispatcher: null,
                id_equipe: null,
                status_extenso: null,
                status_atribuido_extenso: null,
                status_dispatcher_extenso: null,
                nome_usuario_inclusao: null,
                categoria_extenso: null,
                dt_inclusao_extenso: null,
                dt_inclusao: null,
                id_usuario_inclusao: null,
                dt_ultima_alteracao: null,
                id_usuario_ultima_alteracao: null,
                id_usuario_atribuido: null,
                id_usuarios_atribuidos: [],
                nome_usuario_atribuido: null,
                id_equipes: [],
                id_usuarios_visualizadores: [],
                dt_agendamento: null,
                dt_agendamento_extenso: null,
                nome_usuario_solicitante: null,
                telefone_solicitante: null,
                id_empresa: null,
                id_cliente: null,
                id_local_empresa: null,
                endereco: null,
                id_checklist_equipamento: null
            },
            categorias: [],
            tiposTickets: [],
            tiposSolicitacao: [],
            familiasAtendimento: [],
            tiposAtendimento: [],
            servicos: [],
            equipes: [],
            usuariosPorEquipe: [],
            clientes: [],
            vehicleEquipmentPairs: [],
            equipamentosFiltradosPorEmpresa: [],
            veiculosFiltradosPorEmpresaCreate: [],
            equipamentos: [],
            kits: [],
            tiposEquipamentos: [],
            tiposKits: [],
            loadingEquipamentosCreate: false,
            loadingVeiculosCreate: false,
            displayDate: null,
            displayTime: null,
            checklistSelecionado: null,
            currentUserTeams: [],
            isDispatcherInTicketTeams: false,
            isUserTypeZeroInTicketTeams: false
        };
    },
    computed: {
        /** Agendamento só é editável por despachantes e superadmin (mesma regra da tela de Tickets). */
        podeEditarAgendamento() {
            let permissoes = [];
            try {
                permissoes = JSON.parse(sessionStorage.getItem('permissoes') || '[]') || [];
            } catch (e) {
                permissoes = [];
            }
            return getTicketCapabilities(this.user, permissoes).podeEditarAgendamento;
        },
        visibleDetailFields() {
            const list = this.$store.state.config && this.$store.state.config.ticketCamposDetalhe;
            if (!list || !list.length) return null;
            return this.$store.getters['config/ticketCamposDetalheForScreen'](this.assistDetailScreen);
        },
        isProfileAgenciaEmprego() {
            return !!(this.user && this.user.perfil_usuario === 'AgenciaEmprego');
        },
        mergedEquipKitItems() {
            const out = [];
            (this.equipamentosRaw || []).forEach((e) => {
                if (e.ativo !== 1 && e.ativo !== '1') return;
                const id = e.id_equipamento;
                out.push({
                    itemKey: 'e:' + id,
                    displayText: e.codigo_qrcode || e.cod_equipamento || e.nome_equipamento || `EQ ${id}`,
                    raw: { ...e, tipo: 'equipamento' }
                });
            });
            (this.kitsRaw || []).forEach((k) => {
                if (k.ativo !== 1 && k.ativo !== '1') return;
                const id = k.id_kit;
                out.push({
                    itemKey: 'k:' + id,
                    displayText: `[KIT] ${k.nome_kit || k.cod_kit || id}`,
                    raw: { ...k, tipo: 'kit', id_equipamento: id }
                });
            });
            return out.sort((a, b) => (a.displayText || '').localeCompare(b.displayText || ''));
        },
        /** Same numeric id sent to appendtickettequipamento — preselects in detail Equipamentos autocomplete */
        assistPrefillEquipamentoId() {
            if (this.assistAction === 'maintenance' && this.maintenanceRefType === 'vehicle') return null;
            if (!this.selectedItemKey) return null;
            const entry = this.mergedEquipKitItems.find((x) => x.itemKey === this.selectedItemKey);
            if (!entry || entry.raw.id_equipamento == null) return null;
            const n = Number(entry.raw.id_equipamento);
            return Number.isFinite(n) && n > 0 ? n : null;
        },
        /** Step 2 options — add entries here to mount different selects/steps after step 2. */
        assistActionOptions() {
            return [
                { value: 'vehicle', labelKey: 'ticket.assist.associateVehicle' },
                { value: 'maintenance', labelKey: 'ticket.assist.maintenanceAction' }
            ];
        },
        assistStep3LabelKey() {
            return this.assistAction === 'maintenance' ? 'ticket.assist.stepTickets' : 'ticket.assist.stepVehicle';
        },
        assistStep2LabelKey() {
            return this.assistAction === 'maintenance' ? 'ticket.assist.stepMaintenanceResource' : 'ticket.assist.stepEquip';
        },
        showEquipSelectOnStep2() {
            if (this.assistAction === 'vehicle') return true;
            return this.assistAction === 'maintenance' && this.maintenanceRefType === 'equip';
        },
        canProceedFromEquipStep() {
            if (this.assistAction === 'vehicle') return !!this.selectedItemKey;
            if (this.assistAction === 'maintenance') {
                if (this.maintenanceRefType === 'vehicle') {
                    return this.selectedVehicleId != null && this.selectedVehicleId !== '';
                }
                return !!this.selectedItemKey;
            }
            return false;
        },
        /** Shown under step 1 label after the user picks an action. */
        assistActionSummaryText() {
            if (!this.assistAction) return '';
            const opt = this.assistActionOptions.find((o) => o.value === this.assistAction);
            return opt ? this.$t(opt.labelKey) : '';
        },
        /** Shown under step 2: selected equip/kit or vehicle (maintenance by vehicle). */
        assistStep2SelectionSummary() {
            if (!this.assistAction) return '';
            if (this.assistAction === 'maintenance' && this.maintenanceRefType === 'vehicle') {
                if (this.selectedVehicleId == null || this.selectedVehicleId === '') return '';
                const v = this.veiculosList.find((x) => x.id_veiculo == this.selectedVehicleId);
                return v ? v.displayText : String(this.selectedVehicleId);
            }
            if (!this.selectedItemKey) return '';
            const entry = this.mergedEquipKitItems.find((x) => x.itemKey === this.selectedItemKey);
            return entry ? entry.displayText : '';
        },
        /** Step 3 subtitle: vehicle (associate flow) or recap of step 2 (maintenance / tickets list). */
        assistStep3HeaderSummary() {
            if (this.assistAction === 'vehicle') {
                if (this.selectedVehicleId == null || this.selectedVehicleId === '') return '';
                const v = this.veiculosList.find((x) => x.id_veiculo == this.selectedVehicleId);
                return v ? v.displayText : String(this.selectedVehicleId);
            }
            if (this.assistAction === 'maintenance') {
                return this.assistStep2SelectionSummary || '';
            }
            return '';
        },
        /** Step 4 (vehicle flow): same vehicle context as ticket list. */
        assistVehicleFlowTicketsStepSummary() {
            if (this.assistAction !== 'vehicle') return '';
            return this.assistStep3HeaderSummary || '';
        },
        selectedIdEmpresaAssist() {
            if (!this.empresa) return null;
            const cliente = (this.clientes || []).find(
                (c) => c.id_cliente == this.empresa || c.id_cliente === this.empresa
            );
            return cliente ? cliente.id_empresa : this.empresa;
        },
        selectedTipoAtendimentoAssist() {
            if (!this.editedItem || this.editedItem.id_tipo_atendimento == null) return null;
            return (this.tiposAtendimento || []).find(
                (t) => Number(t.id_tipo_atendimento) === Number(this.editedItem.id_tipo_atendimento)
            );
        },
        selectedServiceAssist() {
            if (!this.editedItem || this.editedItem.id_servico == null) return null;
            return (this.servicos || []).find(
                (s) => Number(s.id_servico) === Number(this.editedItem.id_servico)
            );
        },
        selectedServiceUsaVeiculoAssist() {
            const t = this.selectedTipoAtendimentoAssist;
            return t ? t.usa_veiculo === 1 || t.usa_veiculo === true : true;
        },
        selectedServiceUsaEquipamentoAssist() {
            const t = this.selectedTipoAtendimentoAssist;
            return t ? t.usa_equipamento === 1 || t.usa_equipamento === true : true;
        },
        selectedServiceUsaKitAssist() {
            const t = this.selectedTipoAtendimentoAssist;
            return t ? t.usa_kit === 1 || t.usa_kit === true : false;
        },
        checklistForSelectedServiceAssist() {
            const svc = this.selectedServiceAssist;
            if (!svc || !svc.id_checklist_equipamento) return null;
            return this.checklistSelecionado &&
                Number(this.checklistSelecionado.id_checklist_equipamento) === Number(svc.id_checklist_equipamento)
                ? this.checklistSelecionado
                : (this.checklists || []).find(
                      (c) => Number(c.id_checklist_equipamento) === Number(svc.id_checklist_equipamento)
                  ) || null;
        },
        equipamentosFiltradosPorServicoAssist() {
            const list = this.equipamentosFiltradosPorEmpresa || [];
            const t = this.selectedTipoAtendimentoAssist;
            const checklist = this.checklistForSelectedServiceAssist;
            const idTipoEquipamento =
                checklist && checklist.id_tipo_equipamento != null
                    ? parseInt(checklist.id_tipo_equipamento, 10)
                    : null;
            const idTipoKit =
                checklist && checklist.id_tipo_kit != null ? parseInt(checklist.id_tipo_kit, 10) : null;
            const usaEquip = t && (t.usa_equipamento === 1 || t.usa_equipamento === true);
            const usaKit = t && (t.usa_kit === 1 || t.usa_kit === true);
            let result = [];
            if (t) {
                if (!usaEquip && !usaKit) return [];
                if (usaEquip && list.length) {
                    let equipamentos = list.filter((e) => e.tipo === 'equipamento');
                    if (idTipoEquipamento != null && !isNaN(idTipoEquipamento)) {
                        equipamentos = equipamentos.filter(
                            (e) =>
                                e.id_tipo_equipamento != null &&
                                parseInt(e.id_tipo_equipamento, 10) === idTipoEquipamento
                        );
                    }
                    result = result.concat(equipamentos.map((e) => ({ ...e, value: e.id_equipamento })));
                }
                if (usaKit && list.length) {
                    let kits = list.filter((e) => e.tipo === 'kit');
                    if (idTipoKit != null && !isNaN(idTipoKit)) {
                        kits = kits.filter(
                            (e) => e.id_tipo_kit != null && parseInt(e.id_tipo_kit, 10) === idTipoKit
                        );
                    }
                    result = result.concat(kits.map((k) => ({ ...k, value: k.id_equipamento })));
                }
            } else {
                result = (list || []).map((e) => ({ ...e, value: e.id_equipamento }));
            }
            return result;
        },
        vehicleEquipmentPairsValidAssist() {
            return true;
        },
        usuariosFiltradosAssist() {
            // Sem equipe definida a lista fica vazia e o select busca usuarios sob demanda (lazy).
            if (this.editedIndex !== -1) return [];
            if (!Array.isArray(this.usuariosPorEquipe) || this.usuariosPorEquipe.length === 0) return [];
            if (!Array.isArray(this.currentUserTeams) || this.currentUserTeams.length === 0) return [];
            const vistos = new Set();
            const usuarios = [];
            this.usuariosPorEquipe.forEach((ue) => {
                if (!this.currentUserTeams.includes(ue.id_equipe)) return;
                if (ue.id_usuario == null || vistos.has(ue.id_usuario)) return;
                vistos.add(ue.id_usuario);
                usuarios.push({ id_usuario: ue.id_usuario, nome_usuario: ue.nome_usuario });
            });
            return usuarios;
        }
    },
    watch: {
        empresa() {
            const idEmpresa = this.selectedIdEmpresaAssist;
            if (idEmpresa) {
                this.populaEquipamentosPorEmpresaAssist(idEmpresa);
                this.populaVeiculosPorEmpresaAssist(idEmpresa);
                if (!this.vehicleEquipmentPairs || this.vehicleEquipmentPairs.length === 0) {
                    this.vehicleEquipmentPairs = [{ id_veiculo: null, equipamentos: [] }];
                }
            } else {
                this.equipamentosFiltradosPorEmpresa = [];
                this.veiculosFiltradosPorEmpresaCreate = [];
                this.vehicleEquipmentPairs = [];
            }
        },
        drawerOpen(open) {
            if (!open) {
                this.assistDetailScreen = 'colaborador';
            }
        }
    },
    created() {
        try {
            this.user = JSON.parse(sessionStorage.getItem('user') || 'null');
        } catch (_) {
            this.user = null;
        }
        this.token = sessionStorage.getItem('api');
    },
    mounted() {
        this.$store.dispatch('config/fetchTicketCamposDetalhe');
        this.$store.dispatch('config/fetchTicketCamposCriacao');
        if (this.token) {
            this.loadEquipKits();
            this.loadVeiculos();
            this.loadStatus();
            this.loadChecklists();
            this.initAssistTicketCreateDialogData();
        }
    },
    beforeDestroy() {
        if (this.detailSaveTimer) clearTimeout(this.detailSaveTimer);
    },
    methods: {
        filterEquipKit(item, queryText, itemText) {
            const t = (itemText || '').toLowerCase();
            const q = (queryText || '').toLowerCase();
            return t.indexOf(q) > -1;
        },
        async loadEquipKits() {
            this.loadingEquipKits = true;
            try {
                const [er, kr] = await Promise.all([
                    apiController.request(apiController.endpoints.getallEquipamentos, { silent: true }),
                    apiController.request(apiController.endpoints.getallKits, { silent: true })
                ]);
                this.equipamentosRaw = Array.isArray(er.data) ? er.data : [];
                this.kitsRaw = Array.isArray(kr.data) ? kr.data : [];
            } catch (e) {
                EventBus.$emit('show-error', { error: e });
            } finally {
                this.loadingEquipKits = false;
            }
        },
        loadVeiculos() {
            this.loadingVeiculos = true;
            apiController.request(apiController.endpoints.getallVeiculos, {
                silent: true,
                onSuccess: (res) => {
                    let rows = Array.isArray(res.data) ? res.data : [];
                    this.veiculosList = rows
                        .filter((v) => v.ativo === 1 || v.ativo === '1' || v.ativo_extenso === 'Sim')
                        .map((v) => ({
                            ...v,
                            displayText: v.numero_ordem
                                ? `${v.numero_ordem} (${v.placa || ''})`
                                : v.placa || `ID: ${v.id_veiculo}`
                        }));
                    this.loadingVeiculos = false;
                },
                onError: (e) => {
                    EventBus.$emit('show-error', { error: e });
                    this.loadingVeiculos = false;
                },
            });
        },
        loadStatus() {
            apiController.request(apiController.endpoints.getallStatus, {
                silent: true,
                onSuccess: (res) => {
                    const raw = Array.isArray(res.data) ? res.data : [];
                    this.statusList = filterTicketStatusesByPerfil(raw, 'select');
                    this.refreshAssistTickets();
                },
                onError: () => {
                    this.statusList = [];
                },
            });
        },
        loadChecklists() {
            apiController.request(apiController.endpoints.getallChecklistsativos, {
                silent: true,
                onSuccess: (res) => {
                    this.checklists = Array.isArray(res.data) ? res.data : [];
                },
                onError: () => {
                    this.checklists = [];
                },
            });
        },
        recordFirstAssistTime() {
            if (!this.assistEquipSelectedAt) {
                this.assistEquipSelectedAt = new Date();
            }
        },
        onEquipKitSelected() {
            if (this.selectedItemKey) this.recordFirstAssistTime();
        },
        openQrScanner() {
            this.qrScannerError = null;
            this.qrScannerDialog = true;
        },
        closeQrScanner() {
            this.qrScannerDialog = false;
            this.qrScannerError = null;
            this.qrCamera = 'auto';
        },
        onQrInit(promise) {
            promise.catch(() => {
                this.qrScannerError = this.$t('equipmentChecklist.cameraError') || 'Câmera indisponível';
            });
        },
        onQrDecode(decodedString) {
            const list = this.mergedEquipKitItems;
            let item = null;
            try {
                const qrData = JSON.parse(decodedString);
                if (qrData.id_equipamento != null) {
                    item = list.find((x) => x.raw.id_equipamento === qrData.id_equipamento || x.raw.id_equipamento === parseInt(qrData.id_equipamento, 10));
                }
                if (!item && qrData.id_kit != null) {
                    const kid = parseInt(qrData.id_kit, 10);
                    item = list.find((x) => x.itemKey === 'k:' + kid || x.itemKey === 'k:' + qrData.id_kit);
                }
                if (!item && qrData.cod_equipamento) {
                    item = list.find((x) => x.raw.cod_equipamento === qrData.cod_equipamento);
                }
                if (!item && qrData.cod_kit) {
                    item = list.find((x) => x.raw.cod_kit === qrData.cod_kit);
                }
                if (!item && qrData.codigo_qrcode) {
                    item = list.find((x) => x.raw.codigo_qrcode === qrData.codigo_qrcode);
                }
            } catch (_) {
                item = list.find(
                    (x) => x.raw.codigo_qrcode === decodedString || x.raw.cod_equipamento === decodedString || x.raw.cod_kit === decodedString
                );
            }
            if (item) {
                this.selectedItemKey = item.itemKey;
                this.recordFirstAssistTime();
                this.closeQrScanner();
                EventBus.$emit('show-alert', { tipo: 'success', mensagem: this.$t('equipmentChecklist.equipmentSelected') || 'OK' });
            } else {
                this.qrScannerError = this.$t('equipmentChecklist.equipmentNotFound') || 'Item não encontrado na lista.';
            }
        },
        selectAssistAction(action) {
            this.assistAction = action;
            this.maintenanceRefType = 'equip';
            this.selectedItemKey = null;
            this.selectedVehicleId = null;
            this.assistTickets = [];
            this.ticketsError = null;
            // After steps 2–4 mount, set active step or Vuetify can leave the wrong panel visible / hide step 2 content
            this.$nextTick(() => {
                this.wizardStep = 2;
            });
        },
        backFromEquipToAction() {
            this.wizardStep = 1;
            this.assistAction = null;
            this.maintenanceRefType = 'equip';
        },
        onMaintenanceRefTypeChange() {
            if (this.maintenanceRefType === 'equip') {
                this.selectedVehicleId = null;
            } else {
                this.selectedItemKey = null;
            }
        },
        onEquipStepNext() {
            if (this.assistAction === 'vehicle') {
                if (!this.selectedItemKey) return;
                this.wizardStep = 3;
                return;
            }
            if (this.assistAction === 'maintenance') {
                if (this.maintenanceRefType === 'equip') {
                    if (!this.selectedItemKey) return;
                    this.wizardStep = 3;
                    this.fetchTicketsForMaintenance();
                } else {
                    const vid = this.selectedVehicleId;
                    if (vid == null || vid === '') return;
                    this.wizardStep = 3;
                    this.fetchTicketsForVehicle(vid);
                }
            }
        },
        backToAssistActionStep() {
            this.wizardStep = 1;
            this.assistAction = null;
            this.maintenanceRefType = 'equip';
            this.assistTickets = [];
            this.ticketsError = null;
            this.selectedVehicleId = null;
        },
        goToTicketsStep() {
            if (this.assistAction !== 'vehicle') return;
            const vid = this.selectedVehicleId;
            if (vid == null || vid === '') return;
            this.wizardStep = 4;
            this.$nextTick(() => {
                this.fetchTicketsForVehicle(vid);
            });
        },
        onVehicleChanged() {
            this.assistTickets = [];
            this.ticketsError = null;
            if (!this.selectedVehicleId) return;
            this.fetchTicketsForVehicle();
        },
        refreshAssistTickets() {
            if (this.assistAction === 'vehicle' && this.selectedVehicleId) {
                this.fetchTicketsForVehicle();
            } else if (this.assistAction === 'maintenance') {
                if (this.maintenanceRefType === 'vehicle' && this.selectedVehicleId) {
                    this.fetchTicketsForVehicle();
                } else if (this.maintenanceRefType === 'equip') {
                    this.fetchTicketsForMaintenance();
                }
            }
        },
        fetchTicketsForVehicle(vehicleIdOpt) {
            const id =
                vehicleIdOpt != null && vehicleIdOpt !== ''
                    ? vehicleIdOpt
                    : this.selectedVehicleId;
            if (id == null || id === '' || !this.token) return;
            this.loadingTickets = true;
            this.ticketsError = null;
            apiController.request(apiController.endpoints.getTicketsporveiculo, {
                params: { id_veiculo: id },
                silent: true,
                onSuccess: (res) => {
                    if (res.data && res.data.erro) {
                        this.ticketsError = res.data.mensagem || this.$t('ticket.assist.loadTicketsError');
                        this.assistTickets = [];
                        this.loadingTickets = false;
                        return;
                    }
                    let rows = Array.isArray(res.data) ? res.data : [];
                    this.assistTickets = rows;
                    this.loadingTickets = false;
                },
                onError: (e) => {
                    this.ticketsError = this.$t('ticket.assist.loadTicketsError');
                    this.assistTickets = [];
                    EventBus.$emit('show-error', { error: e });
                    this.loadingTickets = false;
                },
            });
        },
        /** Query params for getticketsabertosbyusuario: equipamento direto, kit no ticket, ou equipamento como item de kit. */
        getAssistMaintenanceTicketQueryParams() {
            const params = {};
            const entry = this.mergedEquipKitItems.find((x) => x.itemKey === this.selectedItemKey);
            if (!entry || entry.raw.id_equipamento == null) return params;
            const id = Number(entry.raw.id_equipamento);
            if (!Number.isFinite(id) || id <= 0) return params;
            if (entry.raw.tipo === 'kit') {
                params.id_kit = id;
            } else {
                params.id_equipamento = id;
            }
            return params;
        },
        fetchTicketsForMaintenance() {
            if (!this.token || !this.user || this.user.id_usuario == null) return;
            this.loadingTickets = true;
            this.ticketsError = null;
            const assistParams = this.getAssistMaintenanceTicketQueryParams();
            apiController.request(apiController.endpoints.getTicketsabertosbyusuario, {
                params: { id: this.user.id_usuario },
                query: assistParams,
                silent: true,
                onSuccess: (res) => {
                    if (res.data && res.data.erro) {
                        this.ticketsError = res.data.mensagem || this.$t('ticket.assist.loadTicketsError');
                        this.assistTickets = [];
                        this.loadingTickets = false;
                        return;
                    }
                    let rows = Array.isArray(res.data) ? res.data : [];
                    // API already returns Aberto tickets; do not narrow to first board columns — that hid valid tickets.
                    this.assistTickets = rows;
                    this.loadingTickets = false;
                },
                onError: (e) => {
                    this.ticketsError = this.$t('ticket.assist.loadTicketsError');
                    this.assistTickets = [];
                    EventBus.$emit('show-error', { error: e });
                    this.loadingTickets = false;
                },
            });
        },
        openTicketDetail(ticket) {
            this.assistDetailScreen = 'colaborador';
            this.selectedTicket = { ...ticket };
            this.drawerOpen = true;
            this.$nextTick(() => {
                this.appendAssistEquipmentToTicket(ticket && ticket.id_ticket);
            });
        },
        /**
         * Links wizard-selected equip/kit to the ticket (INSERT IGNORE server-side).
         * Resolves after the request finishes (success, API erro, or network error) so callers can chain saves.
         */
        appendAssistEquipmentToTicketAsync(id_ticket) {
            if (!this.token || !id_ticket || !this.selectedItemKey) {
                return Promise.resolve();
            }
            const entry = this.mergedEquipKitItems.find((x) => x.itemKey === this.selectedItemKey);
            if (!entry || entry.raw.id_equipamento == null) {
                return Promise.resolve();
            }
            const id_eq = Number(entry.raw.id_equipamento);
            if (!Number.isFinite(id_eq) || id_eq <= 0) {
                return Promise.resolve();
            }
            const isKit = entry.raw.tipo === 'kit';
            const body = { id_ticket, id_equipamento: id_eq };
            if (isKit) {
                body.tipo = 'kit';
                const itk = entry.raw.id_tipo_kit != null ? Number(entry.raw.id_tipo_kit) : null;
                if (Number.isFinite(itk) && itk > 0) body.id_tipo_kit = itk;
            }
            return apiController.request(apiController.endpoints.appendTickettequipamento, {
                data: body,
                onSuccess: (res) => {
                    if (res.data && res.data.erro) {
                        EventBus.$emit('show-alert', {
                            tipo: 'error',
                            mensagem: res.data.mensagem || this.$t('ticket.assist.assocEquipError')
                        });
                        return;
                    }
                    const drawer = this.$refs.ticketDetailDrawer;
                    if (drawer && typeof drawer.loadFullDetail === 'function') {
                        drawer.loadFullDetail();
                    }
                },
            }).catch(() => {});
        },
        appendAssistEquipmentToTicket(id_ticket) {
            this.appendAssistEquipmentToTicketAsync(id_ticket);
        },
        /** Ensures assist equip/kit is persisted before debounced updaticket (avoids race with status-only save). */
        persistAssistEquipmentThen(fn) {
            const id = this.selectedTicket && this.selectedTicket.id_ticket;
            this.appendAssistEquipmentToTicketAsync(id).then(() => {
                if (typeof fn === 'function') fn();
            });
        },
        queueDetailSave(patch) {
            if (this.isProfileAgenciaEmprego) return;
            if (!patch || !this.selectedTicket || !this.selectedTicket.id_ticket) return;
            this.selectedTicket = { ...this.selectedTicket, ...patch };
            this.detailPendingPatch = { ...(this.detailPendingPatch || {}), ...patch };
            if (this.detailSaveTimer) clearTimeout(this.detailSaveTimer);
            this.detailSaveTimer = setTimeout(() => {
                this.detailSaveTimer = null;
                this.saveDetailChange();
            }, 400);
        },
        async saveDetailChange() {
            if (this.isProfileAgenciaEmprego) {
                this.detailPendingPatch = null;
                return;
            }
            if (this.detailSaving) {
                this.queueDetailSave(this.detailPendingPatch || {});
                return;
            }
            if (!this.selectedTicket || !this.selectedTicket.id_ticket || !this.detailPendingPatch) return;
            const payload = {
                ...this.selectedTicket,
                ...this.detailPendingPatch,
                id_ticket: this.selectedTicket.id_ticket,
                id_usuario_ultima_alteracao: this.user && this.user.id_usuario
            };
            if (payload.dt_prevista_fim != null) {
                payload.dt_prevista_fim = this.formatDateToMySQLAssist(payload.dt_prevista_fim);
            }
            if (payload.dt_agendamento != null) {
                payload.dt_agendamento = this.formatDateToMySQLAssist(payload.dt_agendamento);
            }
            if (payload.dt_inicio != null) {
                payload.dt_inicio = this.formatDateToMySQLAssist(payload.dt_inicio);
            }
            if (payload.dt_fim != null) {
                payload.dt_fim = this.formatDateToMySQLAssist(payload.dt_fim);
            }
            this.detailSaving = true;
            try {
                const response = await apiController.request(apiController.endpoints.updateTicket, { data: payload, silent: true });
                if (response.data && response.data.erro) {
                    EventBus.$emit('show-alert', {
                        tipo: 'error',
                        mensagem: response.data.mensagem || this.$t('ticket.alerts.error.status')
                    });
                    return;
                }
                this.selectedTicket = { ...payload };
                this.detailPendingPatch = null;
                this.refreshAssistTickets();
                this.$nextTick(() => {
                    if (this.$refs.ticketDetailDrawer && typeof this.$refs.ticketDetailDrawer.loadFullDetail === 'function') {
                        this.$refs.ticketDetailDrawer.loadFullDetail();
                    }
                });
            } catch (e) {
                EventBus.$emit('show-error', { error: e });
            } finally {
                this.detailSaving = false;
            }
        },
        /** Same rules as TicketsJiraView.buildVehicleEquipmentPatch (full equip/kit/veículo payload). */
        buildVehicleEquipmentPatch(pairs, kits) {
            const allEquipamentos = [];
            const allKits = [];
            const allTiposEquipamento = [];
            const allTiposKits = [];
            const allVeiculos = [];
            const seenEquipamentos = new Set();
            const seenKits = new Set();
            const seenTiposEquipamento = new Set();
            const seenTiposKits = new Set();
            const seenVeiculos = new Set();
            (pairs || []).forEach((pair) => {
                if (pair.id_veiculo && !seenVeiculos.has(pair.id_veiculo)) {
                    allVeiculos.push(pair.id_veiculo);
                    seenVeiculos.add(pair.id_veiculo);
                }
                (pair.equipamentos || []).forEach((equipamentoId) => {
                    const idStr = String(equipamentoId);
                    if (idStr.startsWith('te_')) {
                        const id = parseInt(idStr.replace('te_', ''), 10);
                        if (!isNaN(id) && !seenTiposEquipamento.has(id)) {
                            allTiposEquipamento.push(id);
                            seenTiposEquipamento.add(id);
                        }
                        return;
                    }
                    if (idStr.startsWith('tk_')) {
                        const id = parseInt(idStr.replace('tk_', ''), 10);
                        if (!isNaN(id) && !seenTiposKits.has(id)) {
                            allTiposKits.push(id);
                            seenTiposKits.add(id);
                        }
                        return;
                    }
                    const kit = (kits || []).find((k) => k.id_kit === equipamentoId);
                    if (kit) {
                        if (!seenKits.has(kit.id_kit)) {
                            allKits.push({ id_kit: kit.id_kit, id_tipo_kit: kit.id_tipo_kit });
                            seenKits.add(kit.id_kit);
                        }
                    } else if (!seenEquipamentos.has(equipamentoId)) {
                        allEquipamentos.push(equipamentoId);
                        seenEquipamentos.add(equipamentoId);
                    }
                });
            });
            return {
                id_equipamentos: allEquipamentos.length > 0 ? allEquipamentos : [],
                id_kits: allKits.length > 0 ? allKits : [],
                id_tipos_equipamento: allTiposEquipamento.length > 0 ? allTiposEquipamento : [],
                id_tipos_kits: allTiposKits.length > 0 ? allTiposKits : [],
                id_veiculos: allVeiculos.length > 0 ? allVeiculos : []
            };
        },
        onVehicleEquipmentPairsChange(pairs) {
            if (!this.selectedTicket || !this.selectedTicket.id_ticket) return;
            const drawer = this.$refs.ticketDetailDrawer;
            const kits = drawer && Array.isArray(drawer.kits) ? drawer.kits : [];
            const patch = this.buildVehicleEquipmentPatch(pairs, kits);
            this.queueDetailSave(patch);
        },
        onDetailStatusChange(value) {
            this.queueDetailSave({ id_status: value });
        },
        onDetailTeamChange(value) {
            const id = value != null && value !== '' ? Number(value) : null;
            if (id != null && Number.isFinite(id)) {
                this.queueDetailSave({ id_equipe: id, id_equipes: [id] });
            } else {
                this.queueDetailSave({ id_equipe: null, id_equipes: [] });
            }
        },
        onDetailScheduleChange(value) {
            this.queueDetailSave({ dt_agendamento: value });
        },
        onDetailExpectedFinalizationChange(value) {
            this.queueDetailSave({ dt_prevista_fim: value });
        },
        onDetailSlaManualChange(value) {
            this.queueDetailSave({ sla_manual: value || null });
        },
        onDetailUserChange(value) {
            this.queueDetailSave(value || {});
        },
        onDetailTipoTicketChange(value) {
            this.queueDetailSave({
                id_tipo_ticket: value,
                id_tipo_atendimento: null,
                id_servico: null,
                id_checklist_equipamento: null,
                nome_checklist_extenso: null
            });
        },
        onDetailFamiliaChange(value) {
            this.queueDetailSave({
                id_familia_atendimento: value,
                id_tipo_atendimento: null,
                id_servico: null,
                id_checklist_equipamento: null,
                nome_checklist_extenso: null
            });
        },
        onDetailTipoAtendimentoChange(value) {
            this.queueDetailSave({
                id_tipo_atendimento: value,
                id_servico: null,
                id_checklist_equipamento: null,
                nome_checklist_extenso: null
            });
        },
        onDetailServicoChange(id_servico) {
            this.queueDetailSave({ id_servico });
        },
        onStatusAtribuidoChange(value) {
            this.persistAssistEquipmentThen(() => this.queueDetailSave(value || {}));
        },
        onStatusDispatcherChange(value) {
            this.persistAssistEquipmentThen(() => this.queueDetailSave({ id_status_dispatcher: value }));
        },
        onDetailUpdated() {
            this.refreshAssistTickets();
            this.$nextTick(() => {
                if (this.$refs.ticketDetailDrawer && typeof this.$refs.ticketDetailDrawer.loadFullDetail === 'function') {
                    this.$refs.ticketDetailDrawer.loadFullDetail();
                }
            });
        },
        isTicketAssignedToMe(item) {
            if (!item || !this.user) return false;
            const uid = this.user.id_usuario;
            if (uid == null) return false;
            const a = item.id_usuario_atribuido;
            return a != null && (Number(a) === Number(uid) || String(a) === String(uid));
        },
        async onOpenChecklistDialog(item) {
            if (!item) return;
            if (this.user && this.user.is_superadmin === 1) {
                this.currentChecklistTicket = item;
                this.checklistDialog = true;
                return;
            }
            if (!this.isTicketAssignedToMe(item)) {
                EventBus.$emit('show-alert', {
                    tipo: 'error',
                    mensagem: this.$t('ticket.checklist.error.onlyAssignedUserCanFulfill')
                });
                return;
            }
            this.currentChecklistTicket = item;
            this.checklistDialog = true;
        },
        onChecklistSaved() {
            const drawer = this.$refs.ticketDetailDrawer;
            if (drawer && typeof drawer.requestOpenChangeStatusWhenDetailLoaded === 'function') {
                drawer.requestOpenChangeStatusWhenDetailLoaded();
            }
            this.onDetailUpdated();
        },
        initAssistTicketCreateDialogData() {
            if (!this.token) return;
            this.PopulaCategoriasAssist();
            this.PopulaEquipesAssist();
            this.PopulaClientesAssist();
            this.PopulaTiposEquipamentosAssist();
            this.PopulaTiposKitsAssist();
            this.PopulaTiposTicketsAssist();
            this.PopulaTiposSolicitacaoAssist();
            this.PopulaFamiliasAtendimentoAssist();
            this.checkUserInAnyTeamAssist();
        },
        getFirstStatusIdByOrderAssist() {
            const list = (this.statusList || [])
                .map((s) => ({
                    id: Number(s.id_status_ticket),
                    order: s.ordem != null ? Number(s.ordem) : Number(s.id_status_ticket)
                }))
                .filter((s) => !Number.isNaN(s.id) && !Number.isNaN(s.order))
                .sort((a, b) => (a.order - b.order) || (a.id - b.id));
            return list.length > 0 ? list[0].id : 1;
        },
        formatDateToMySQLAssist(dateValue) {
            if (!dateValue) return null;
            if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateValue)) {
                return dateValue;
            }
            const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
            if (isNaN(date.getTime())) return null;
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        },
        PopulaCategoriasAssist() {
            apiController.request(apiController.endpoints.getallCategorias, {
                onSuccess: (response) => {
                    this.categorias = response.data;
                },
            });
        },
        PopulaEquipesAssist() {
            apiController.request(apiController.endpoints.getallEquipes, {
                onSuccess: (response) => {
                    this.equipes = response.data.filter((e) => e.ativo === 1 || e.ativo_extenso === 'Sim');
                },
            });
        },
        PopulaClientesAssist() {
            apiController.request(apiController.endpoints.getallClientes, {
                onSuccess: (response) => {
                    this.clientes = (response.data || []).filter((c) => c.ativo === 1);
                },
            });
        },
        PopulaTiposEquipamentosAssist() {
            apiController.request(apiController.endpoints.getallTiposequipamentos, {
                silent: true,
                onSuccess: (response) => {
                    this.tiposEquipamentos = response.data || [];
                },
                onError: () => {
                    this.tiposEquipamentos = [];
                },
            });
        },
        PopulaTiposKitsAssist() {
            apiController.request(apiController.endpoints.getallTiposkits, {
                silent: true,
                onSuccess: (response) => {
                    this.tiposKits = response.data || [];
                },
                onError: () => {
                    this.tiposKits = [];
                },
            });
        },
        PopulaTiposTicketsAssist() {
            apiController.request(apiController.endpoints.getallTipostickets, {
                onSuccess: (response) => {
                    this.tiposTickets = response.data;
                },
            });
        },
        PopulaTiposSolicitacaoAssist() {
            apiController.request(apiController.endpoints.getTipossolicitacao, {
                onSuccess: (response) => {
                    this.tiposSolicitacao = response.data;
                },
                onError: () => {
                    this.tiposSolicitacao = [];
                },
            });
        },
        PopulaFamiliasAtendimentoAssist() {
            apiController.request(apiController.endpoints.getFamiliasatendimento, {
                onSuccess: (response) => {
                    this.familiasAtendimento = response.data;
                },
                onError: () => {
                    this.familiasAtendimento = [];
                },
            });
        },
        PopulaTiposAtendimentoAssist(id_familia_atendimento, id_tipo_ticket) {
            const familia = (this.familiasAtendimento || []).find(
                (f) => f.id_familia_atendimento == id_familia_atendimento
            );
            if (familia && familia.id_equipe != null) {
                this.$set(this.editedItem, 'id_equipe', familia.id_equipe);
                this.editedItem.id_equipes = [familia.id_equipe];
                this.$nextTick(() => {
                    if (this.editedItem.id_equipe) {
                        this.fetchUsuariosPorEquipesAssist([this.editedItem.id_equipe]);
                        this.checkDispatcherInTeamsAssist([this.editedItem.id_equipe]);
                    }
                });
            } else {
                this.$set(this.editedItem, 'id_equipe', null);
                this.editedItem.id_equipes = [];
            }
            if (!id_familia_atendimento) {
                this.tiposAtendimento = [];
                return;
            }
            apiController.request(apiController.endpoints.getTiposatendimento, {
                query: {
                    id_familia_atendimento,
                    ...(id_tipo_ticket ? { id_tipo_ticket } : {})
                },
                onSuccess: (response) => {
                    this.tiposAtendimento = response.data;
                },
                onError: () => {
                    this.tiposAtendimento = [];
                },
            });
        },
        PopulaServicosAssist(id_tipo_atendimento) {
            if (!id_tipo_atendimento) {
                this.servicos = [];
                return;
            }
            apiController.request(apiController.endpoints.getServicos, {
                query: { id_tipo_atendimento },
                onSuccess: (response) => {
                    this.servicos = response.data;
                },
                onError: () => {
                    this.servicos = [];
                },
            });
        },
        async fetchUsuariosPorEquipesAssist(teamIds) {
            this.usuariosPorEquipe = [];
            const fetchPromises = teamIds.map(async (teamId) => {
                try {
                    const response = await apiController.request(apiController.endpoints.getUsuariosporequipe, { params: { id: teamId }, silent: true });
                    return response.data.map((u) => ({ ...u, id_equipe: teamId }));
                } catch (e) {
                    return [];
                }
            });
            const results = await Promise.all(fetchPromises);
            const uniqueUsers = [];
            const seenUsers = new Set();
            results.flat().forEach((ue) => {
                const key = `${ue.id_equipe}-${ue.id_usuario}`;
                if (!seenUsers.has(key)) {
                    seenUsers.add(key);
                    uniqueUsers.push(ue);
                }
            });
            this.usuariosPorEquipe = uniqueUsers;
            if (this.user && this.user.id_usuario && teamIds && teamIds.length > 0) {
                this.checkDispatcherInTeamsAssist(teamIds);
            }
        },
        checkDispatcherInTeamsAssist(teamIds) {
            if (!this.user || !this.user.id_usuario || !teamIds || teamIds.length === 0) {
                this.isDispatcherInTicketTeams = false;
                this.isUserTypeZeroInTicketTeams = false;
                return;
            }
            if (Array.isArray(this.usuariosPorEquipe) && this.usuariosPorEquipe.length > 0) {
                const isDispatcher = this.usuariosPorEquipe.some(
                    (ue) =>
                        teamIds.includes(ue.id_equipe) &&
                        ue.id_usuario === this.user.id_usuario &&
                        (ue.id_tipo_usuario === 2 ||
                            ue.id_tipo_usuario === '2' ||
                            ue.is_dispatcher === 'Sim')
                );
                this.isDispatcherInTicketTeams = isDispatcher;
                const isTypeZero = this.usuariosPorEquipe.some(
                    (ue) =>
                        teamIds.includes(ue.id_equipe) &&
                        ue.id_usuario === this.user.id_usuario &&
                        (ue.id_tipo_usuario === 0 || ue.id_tipo_usuario === '0')
                );
                this.isUserTypeZeroInTicketTeams = isTypeZero;
            }
        },
        async checkUserInAnyTeamAssist() {
            if (!this.user || !this.user.id_usuario) {
                this.currentUserTeams = [];
                return;
            }
            try {
                const response = await apiController.request(apiController.endpoints.getallEquipes, { silent: true });
                const teams = response.data.filter((e) => e.ativo === 1 || e.ativo_extenso === 'Sim');
                if (teams.length === 0) {
                    this.currentUserTeams = [];
                    return;
                }
                const checkPromises = teams.map(async (team) => {
                    try {
                        const teamUsersResponse = await apiController.request(apiController.endpoints.getUsuariosporequipe, { params: { id: team.id_equipe }, silent: true });
                        return {
                            teamId: team.id_equipe,
                            isInTeam: teamUsersResponse.data.some((u) => u.id_usuario === this.user.id_usuario)
                        };
                    } catch (e) {
                        return { teamId: team.id_equipe, isInTeam: false };
                    }
                });
                const results = await Promise.all(checkPromises);
                this.currentUserTeams = results
                    .filter((r) => r.isInTeam === true)
                    .map((r) => r.teamId);
            } catch (e) {
                this.currentUserTeams = [];
            }
        },
        populaEquipamentosPorEmpresaAssist(idEmpresa) {
            if (!idEmpresa) {
                this.equipamentosFiltradosPorEmpresa = [];
                return;
            }
            this.loadingEquipamentosCreate = true;
            const filterAndFormat = () => {
                const equipamentosFiltrados = (this.equipamentos || [])
                    .filter(
                        (e) => parseInt(e.id_empresa) === parseInt(idEmpresa) && e.ativo === 1
                    )
                    .map((e) => ({
                        ...e,
                        tipo: 'equipamento',
                        displayText:
                            e.codigo_qrcode || e.cod_equipamento || e.nome_equipamento || `ID: ${e.id_equipamento}`
                    }));
                const kitsFiltrados = (this.kits || [])
                    .filter((k) => parseInt(k.id_empresa) === parseInt(idEmpresa) && k.ativo === 1)
                    .map((k) => ({
                        ...k,
                        id_equipamento: k.id_kit,
                        nome_equipamento: k.nome_kit || k.cod_kit,
                        cod_equipamento: k.cod_kit,
                        id_tipo_kit: k.id_tipo_kit,
                        tipo: 'kit',
                        displayText: `[KIT] ${k.nome_kit || k.cod_kit} (${k.cod_kit})`
                    }));
                this.equipamentosFiltradosPorEmpresa = [...kitsFiltrados, ...equipamentosFiltrados];
                this.loadingEquipamentosCreate = false;
            };
            const equipamentosLoaded = this.equipamentos && this.equipamentos.length > 0;
            const kitsLoaded = this.kits && this.kits.length > 0;
            if (equipamentosLoaded && kitsLoaded) {
                filterAndFormat();
            } else {
                const promises = [];
                if (!equipamentosLoaded) {
                    promises.push(
                        (async () => {
                            const response = await apiController.request(apiController.endpoints.getallEquipamentos, { silent: true });
                            this.equipamentos = response.data || [];
                        })()
                    );
                }
                if (!kitsLoaded) {
                    promises.push(
                        (async () => {
                            const response = await apiController.request(apiController.endpoints.getallKits, { silent: true });
                            this.kits = response.data || [];
                        })()
                    );
                }
                Promise.all(promises)
                    .then(() => filterAndFormat())
                    .catch((e) => {
                        this.loadingEquipamentosCreate = false;
                        EventBus.$emit('show-error', { error: e });
                    });
            }
        },
        populaVeiculosPorEmpresaAssist(idEmpresa) {
            if (!idEmpresa) {
                this.veiculosFiltradosPorEmpresaCreate = [];
                return;
            }
            this.loadingVeiculosCreate = true;
            apiController.request(apiController.endpoints.getVeiculoporempresa, {
                params: { id: idEmpresa },
                silent: true,
                onSuccess: (response) => {
                    this.veiculosFiltradosPorEmpresaCreate = (response.data || [])
                        .filter((v) => v.ativo === 1 || v.ativo_extenso === 'Sim')
                        .map((v) => ({
                            ...v,
                            displayText: v.numero_ordem
                                ? `${v.numero_ordem} (${v.placa || ''})`
                                : v.placa || `ID: ${v.id_veiculo}`
                        }));
                    this.loadingVeiculosCreate = false;
                },
                onError: (e) => {
                    this.loadingVeiculosCreate = false;
                    EventBus.$emit('show-error', { error: e });
                },
            });
        },
        getEquipamentoDisplayTextByIdAssist(id) {
            const idStr = id != null ? String(id) : '';
            if (idStr.startsWith('te_')) {
                const tipoId = parseInt(idStr.replace('te_', ''), 10);
                const t = (this.tiposEquipamentos || []).find(
                    (te) => parseInt(te.id_tipo_equipamento, 10) === tipoId
                );
                return t ? t.nome_tipo_equipamento || 'Tipo ' + tipoId : idStr;
            }
            if (idStr.startsWith('tk_')) {
                const tipoId = parseInt(idStr.replace('tk_', ''), 10);
                const t = (this.tiposKits || []).find((tk) => parseInt(tk.id_tipo_kit, 10) === tipoId);
                return t ? (t.nome_kit || 'Tipo kit ' + tipoId) + ' [tipo]' : idStr;
            }
            const item = this.equipamentosFiltradosPorServicoAssist.find(
                (e) => e.value === id || e.id_equipamento === id
            );
            if (item && item.displayText) return item.displayText;
            const itemEmpresa = this.equipamentosFiltradosPorEmpresa.find((e) => e.id_equipamento === id);
            if (itemEmpresa && itemEmpresa.displayText) return itemEmpresa.displayText;
            const equipamento = (this.equipamentos || []).find((e) => e.id_equipamento === id);
            if (equipamento) {
                return (
                    equipamento.codigo_qrcode ||
                    equipamento.cod_equipamento ||
                    equipamento.nome_equipamento ||
                    `ID: ${id}`
                );
            }
            const kit = (this.kits || []).find((k) => k.id_kit === id);
            if (kit) {
                return `[KIT] ${kit.nome_kit || kit.cod_kit} (${kit.cod_kit})`;
            }
            return `ID: ${id}`;
        },
        addVehiclePairAssist() {
            this.vehicleEquipmentPairs.push({ id_veiculo: null, equipamentos: [] });
        },
        removeVehiclePairAssist(index) {
            if (this.vehicleEquipmentPairs.length > 1) {
                this.vehicleEquipmentPairs.splice(index, 1);
            }
        },
        duplicateVehiclePairAssist(index) {
            const pair = this.vehicleEquipmentPairs[index];
            const clone = {
                id_veiculo: pair.id_veiculo != null ? pair.id_veiculo : null,
                equipamentos: Array.isArray(pair.equipamentos) ? [...pair.equipamentos] : []
            };
            this.vehicleEquipmentPairs.splice(index + 1, 0, clone);
        },
        async openAssistNewTicketDialog() {
            this.editedIndex = -1;
            this.editedItem = {
                id_ticket: null,
                titulo: '',
                corpo: '',
                id_categoria: null,
                id_status: null,
                status_extenso: null,
                nome_usuario_inclusao: null,
                categoria_extenso: null,
                dt_inclusao_extenso: null,
                dt_inclusao: null,
                id_usuario_inclusao: null,
                dt_ultima_alteracao: null,
                id_usuario_ultima_alteracao: null,
                id_usuario_atribuido: null,
                nome_usuario_atribuido: null,
                id_equipes: [],
                id_usuarios_visualizadores: [],
                dt_agendamento: null,
                dt_agendamento_extenso: null
            };
            this.usuariosPorEquipe = [];
            this.isDispatcherInTicketTeams = false;
            this.isUserTypeZeroInTicketTeams = false;
            this.empresa = this.user && this.user.id_cliente != null ? this.user.id_cliente : null;
            this.vehicleEquipmentPairs = [];
            this.createTicketDialog = true;
        },
        closeAssistCreateTicketDialog() {
            this.createTicketDialog = false;
            this.loadingSave = false;
            this.csvErrorsOpen = false;
            this.vehicleEquipmentPairs = [];
            this.equipamentosFiltradosPorEmpresa = [];
            this.veiculosFiltradosPorEmpresaCreate = [];
            this.empresa = null;
        },
        saveAssistNewTicket(saveAndAddAnother = false) {
            this.editedItem.id_equipes = this.editedItem.id_equipe ? [this.editedItem.id_equipe] : [];
            this.loadingSave = true;
            this.editedItem.id_usuario_inclusao = this.user.id_usuario;
            const firstStatusId = this.getFirstStatusIdByOrderAssist();
            this.editedItem.id_status_atribuido = firstStatusId;
            this.editedItem.id_status_dispatcher = firstStatusId;
            this.operacao = 0;
            if (!this.editedItem.titulo || this.editedItem.titulo.trim() === '') {
                if (this.editedItem.corpo && this.editedItem.corpo.trim()) {
                    this.editedItem.titulo = this.editedItem.corpo.substring(0, 100);
                } else {
                    this.editedItem.titulo = '';
                }
            }
            const itemToSend = Object.assign({}, this.editedItem);
            if (itemToSend.dt_agendamento instanceof Date) {
                itemToSend.dt_agendamento = this.formatDateToMySQLAssist(itemToSend.dt_agendamento);
            }
            itemToSend.id_cliente =
                this.empresa != null
                    ? this.empresa
                    : this.editedItem.id_cliente != null
                      ? this.editedItem.id_cliente
                      : null;
            const ticketCombinations = [];
            (this.vehicleEquipmentPairs || []).forEach((pair) => {
                if (!pair) return;
                const equipamentos = [];
                const kits = [];
                const tiposEquipamento = [];
                const tiposKits = [];
                if (pair.equipamentos && Array.isArray(pair.equipamentos) && pair.equipamentos.length > 0) {
                    (pair.equipamentos || []).forEach((equipamentoId) => {
                        const idStr = String(equipamentoId);
                        if (idStr.startsWith('te_')) {
                            const id = parseInt(idStr.replace('te_', ''), 10);
                            if (!isNaN(id)) tiposEquipamento.push(id);
                            return;
                        }
                        if (idStr.startsWith('tk_')) {
                            const id = parseInt(idStr.replace('tk_', ''), 10);
                            if (!isNaN(id)) tiposKits.push(id);
                            return;
                        }
                        const kit = (this.kits || []).find((k) => k.id_kit === equipamentoId);
                        if (kit) {
                            kits.push({ id_kit: kit.id_kit, id_tipo_kit: kit.id_tipo_kit });
                        } else {
                            equipamentos.push(equipamentoId);
                        }
                    });
                }
                ticketCombinations.push({
                    id_equipamentos: equipamentos,
                    id_kits: kits,
                    id_tipos_equipamento: tiposEquipamento,
                    id_tipos_kits: tiposKits,
                    id_veiculos: pair.id_veiculo ? [pair.id_veiculo] : []
                });
            });
            if (ticketCombinations.length === 0) {
                itemToSend.id_equipamentos = [];
                itemToSend.id_kits = [];
                itemToSend.id_tipos_equipamento = [];
                itemToSend.id_tipos_kits = [];
                itemToSend.id_veiculos = [];
                apiController.request(apiController.endpoints.insertTicket, {
                    data: itemToSend,
                    silent: true,
                    onSuccess: (response) => {
                        this.handleAssistSaveResponse(response, saveAndAddAnother);
                    },
                    onError: (e) => {
                        this.loadingSave = false;
                        EventBus.$emit('show-error', { error: e });
                    },
                });
            } else if (ticketCombinations.length === 1) {
                const combination = ticketCombinations[0];
                itemToSend.id_equipamentos = combination.id_equipamentos || [];
                itemToSend.id_kits = combination.id_kits || [];
                itemToSend.id_tipos_equipamento = combination.id_tipos_equipamento || [];
                itemToSend.id_tipos_kits = combination.id_tipos_kits || [];
                itemToSend.id_veiculos = combination.id_veiculos || [];
                apiController.request(apiController.endpoints.insertTicket, {
                    data: itemToSend,
                    silent: true,
                    onSuccess: (response) => {
                        this.handleAssistSaveResponse(response, saveAndAddAnother);
                    },
                    onError: (e) => {
                        this.loadingSave = false;
                        EventBus.$emit('show-error', { error: e });
                    },
                });
            } else {
                this.createMultipleTicketsAssist(itemToSend, ticketCombinations, saveAndAddAnother);
            }
        },
        createMultipleTicketsAssist(baseTicket, combinations, saveAndAddAnother) {
            let completed = 0;
            let firstCreatedId = null;
            const errors = [];
            const total = combinations && combinations.length > 0 ? combinations.length : 0;
            if (total === 0) {
                this.loadingSave = false;
                EventBus.$emit('show-alert', {
                    tipo: 'warning',
                    mensagem: this.$t('ticket.noTicketsToCreate')
                });
                return;
            }
            EventBus.$emit('show-alert', {
                tipo: 'info',
                mensagem: this.$t('ticket.creatingTickets', { total })
            });
            combinations.forEach((combination, index) => {
                const ticketToSend = Object.assign({}, baseTicket);
                ticketToSend.id_equipamentos = combination.id_equipamentos;
                ticketToSend.id_kits = combination.id_kits || [];
                ticketToSend.id_tipos_equipamento = combination.id_tipos_equipamento || [];
                ticketToSend.id_tipos_kits = combination.id_tipos_kits || [];
                ticketToSend.id_veiculos = combination.id_veiculos;
                ticketToSend.id_servico = baseTicket.id_servico != null ? baseTicket.id_servico : null;
                apiController.request(apiController.endpoints.insertTicket, {
                    data: ticketToSend,
                    silent: true,
                    onSuccess: (response) => {
                        completed++;
                        if (response.data && response.data.erro) {
                            errors.push(
                                this.$t('ticket.errorCreatingTicket', {
                                    index: index + 1,
                                    mensagem: response.data.mensagem
                                })
                            );
                        } else {
                            const tid = this.extractInsertTicketId(response);
                            if (tid != null && firstCreatedId == null) {
                                firstCreatedId = tid;
                            }
                        }
                        if (completed === total) {
                            this.loadingSave = false;
                            if (errors.length > 0) {
                                const successCount = total - errors.length;
                                EventBus.$emit('show-alert', {
                                    tipo: 'warning',
                                    mensagem: this.$t('ticket.successCreatingTickets', {
                                        total: successCount,
                                        totalTotal: total,
                                        errors: errors.join('; ')
                                    })
                                });
                            } else {
                                EventBus.$emit('show-alert', {
                                    tipo: 'success',
                                    mensagem: this.$t('ticket.successCreatingTicketsAll', { total })
                                });
                            }
                            this.afterAssistTicketCreated(saveAndAddAnother, firstCreatedId);
                        }
                    },
                    onError: (e) => {
                        completed++;
                        const errorMsg = (e.response && e.response.data && e.response.data.mensagem) || e.message;
                        errors.push(
                            this.$t('ticket.errorCreatingTicket', { index: index + 1, mensagem: errorMsg })
                        );
                        if (completed === total) {
                            this.loadingSave = false;
                            if (errors.length === total) {
                                EventBus.$emit('show-error', {
                                    error: new Error(
                                        this.$t('ticket.errorCreatingTickets', { errors: errors.join('; ') })
                                    )
                                });
                            } else {
                                const successCount = total - errors.length;
                                EventBus.$emit('show-alert', {
                                    tipo: 'warning',
                                    mensagem: this.$t('ticket.successCreatingTickets', {
                                        total: successCount,
                                        totalTotal: total,
                                        errors: errors.join('; ')
                                    })
                                });
                            }
                            this.afterAssistTicketCreated(saveAndAddAnother, firstCreatedId);
                        }
                    },
                });
            });
        },
        extractInsertTicketId(response) {
            if (!response || !response.data) return null;
            const d = response.data;
            if (d.erro) return null;
            if (d.id_ticket != null) return d.id_ticket;
            if (d.insertId != null) return d.insertId;
            if (d.dados && d.dados.id_ticket != null) return d.dados.id_ticket;
            return null;
        },
        handleAssistSaveResponse(response, saveAndAddAnother) {
            this.loadingSave = false;
            if (response && response.data && response.data.erro) {
                EventBus.$emit('show-alert', {
                    tipo: 'error',
                    mensagem: response.data.mensagem || this.$t('ticket.alerts.error.status')
                });
                return;
            }
            if (!saveAndAddAnother) {
                EventBus.$emit('show-alert', {
                    mensagem: this.$t('ticket.updateTable.success.insert'),
                    tipo: 'success'
                });
            }
            const createdId = this.extractInsertTicketId(response);
            this.afterAssistTicketCreated(saveAndAddAnother, createdId);
        },
        afterAssistTicketCreated(saveAndAddAnother, createdTicketId) {
            if (saveAndAddAnother) {
                this.openAssistNewTicketDialog();
                return;
            }
            this.closeAssistCreateTicketDialog();
            this.refreshAssistTickets();
            this.$root.$emit('atualizarTicketsAbertos');
            if (createdTicketId != null && createdTicketId !== '') {
                this.detailPendingPatch = null;
                this.assistDetailScreen = 'supervisor';
                this.selectedTicket = { id_ticket: createdTicketId };
                this.$nextTick(() => {
                    this.drawerOpen = true;
                });
            }
        },
        onAgencyTicketSaved(payload) {
            const id = payload && payload.id_ticket != null ? payload.id_ticket : null;
            this.afterAssistTicketCreated(false, id);
        }
    }
};
</script>

<style scoped>
.ticket-assist-page {
    max-width: 560px;
    margin: 0 auto;
}

/* Do not set overflow:visible on .v-stepper__content — it breaks Vuetify hiding inactive panels and can cover step 2 (e.g. equipment autocomplete). Radio ripples: use padding on the group only. */
.ticket-assist-page ::v-deep .maintenance-ref-type-radios {
    padding: 8px 8px 10px 12px;
    margin: 2px 0 4px;
}
.qr-wrap {
    position: relative;
    width: 100%;
    max-width: 480px;
    margin: 0 auto;
}

.ticket-assist-page ::v-deep .equip-kit-autocomplete .v-label {
    white-space: normal !important;
    overflow-wrap: break-word;
    word-break: break-word;
    text-overflow: clip !important;
    overflow: visible !important;
    line-height: 1.2;
    max-width: calc(100% - 56px);
    top: 10px;
}

@media screen and (max-width: 416px) {
    .ticket-assist-page ::v-deep .equip-kit-autocomplete .v-label {
        max-width: calc(100% - 56px);
    }

    .ticket-assist-page ::v-deep .equip-kit-autocomplete .v-input__slot {
        min-height: 60px !important;
        padding-bottom: 6px;
        align-items: center;
    }

    .ticket-assist-page ::v-deep .equip-kit-autocomplete .v-select__slot {
        align-items: center;
    }
}

@media screen and (max-width: 343px) {
    .ticket-assist-page ::v-deep .equip-kit-autocomplete .v-label {
        max-width: calc(100% - 56px);
    }

    .ticket-assist-page ::v-deep .equip-kit-autocomplete .v-input__slot {
        min-height: 78px !important;
        padding-bottom: 6px;
        align-items: center;
    }

    .ticket-assist-page ::v-deep .equip-kit-autocomplete .v-select__slot {
        align-items: center;
    }
}

.stepper-step-title {
    line-height: 1.35;
}
.stepper-step-selected {
    margin-top: 4px;
    max-width: min(100%, 280px);
    line-height: 1.35;
    word-break: break-word;
}

.ticket-assist-page ::v-deep .equip-kit-autocomplete .v-input__slot {
    height: auto !important;
    padding-top: 20px;
    padding-bottom: 10px;
    align-items: flex-start;
}

.ticket-assist-page ::v-deep .equip-kit-autocomplete .v-select__slot {
    align-items: flex-start;
    min-height: 100%;
}
</style>
